import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  advanceNavigation,
  createNavigationCursor,
  getMenuActions,
  injectActions,
  previewSstCommands,
} from '../lib/sstRuntime/sstNavigationEngine'
import {
  getFieldBinding,
  isSstCheckboxChecked,
  mergeFormDefaults,
  validateSstForm,
} from '../lib/sstRuntime/sstFieldRuntime'
import './SstInterpreterPreview.css'

function normalizeTarget(value = '') {
  return String(value).trim().replace(/:$/, '').toUpperCase()
}

function normalizeVariable(value = '') {
  return String(value).trim().replace(/^@/, '').replace(/#[A-Z]$/i, '').toUpperCase()
}

function safeMenuValue(value = '') {
  return String(value)
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function menuWithActionRefs(menu = {}) {
  const menuId = safeMenuValue(menu.id || menu.key || menu.title || 'MENU') || 'MENU'
  const items = (menu.items || menu.options || []).map((item) => ({
    ...item,
    actionRef: item.actionRef || `${menuId}_OPTION_${safeMenuValue(item.text || item.value)}`,
    submenu: item.submenu ? menuWithActionRefs(item.submenu) : item.submenu,
  }))
  return { ...menu, items }
}

function collectMenus(ir = {}) {
  const roots = ir.menuTree?.length ? ir.menuTree : ir.menus || []
  const menus = []
  const seen = new Set()
  const visit = (menu) => {
    if (!menu) return
    const identity = menu.id || menu.key || menu.title
    if (identity && seen.has(identity)) return
    if (identity) seen.add(identity)
    if (menu.visible !== false) menus.push(menu)
    ;(menu.items || menu.options || []).forEach((item) => {
      if (item.submenu) visit(item.submenu)
      ;(item.actions || []).forEach((action) => {
        if (action.type === 'submenu' && action.menu) visit(action.menu)
      })
    })
  }
  roots.filter((menu) => !menu.parentKey).forEach(visit)
  return menus
}

function buildScreens(ir = {}) {
  const menus = collectMenus(ir).map((menu) => ({
    type: 'menu',
    id: menu.id || menu.key,
    title: menu.title,
    value: menuWithActionRefs(menu),
  }))
  const forms = (ir.forms || [])
    .filter((form) => form.visible !== false)
    .map((form) => ({ type: 'form', id: form.id, title: form.title, value: form }))
  return [...menus, ...forms].sort((left, right) => {
    const leftLine = left.value.line ?? left.value.sourceLine ?? Number.MAX_SAFE_INTEGER
    const rightLine = right.value.line ?? right.value.sourceLine ?? Number.MAX_SAFE_INTEGER
    return leftLine - rightLine
  })
}

function findTargetIndex(screens, request = {}) {
  const targets = [request.target, request.legacyTarget, request.title]
    .filter(Boolean)
    .map(normalizeTarget)
  const expectedType = request.type === 'showMenu' ? 'menu' : 'form'
  for (const target of targets) {
    const index = screens.findIndex((screen) => {
      if (screen.type !== expectedType) return false
      return [screen.id, screen.value?.id, screen.value?.legacyId, screen.value?.key, screen.value?.title]
        .filter(Boolean)
        .map(normalizeTarget)
        .includes(target)
    })
    if (index >= 0) return index
  }
  return -1
}

function fieldVariables(field = {}) {
  if (field.type === 'field_row') return (field.parts || []).flatMap(fieldVariables)
  if (field.type === 'checkbox-group') return (field.options || []).flatMap(fieldVariables)
  const binding = normalizeVariable(getFieldBinding(field))
  return binding ? [binding] : []
}

function findCaptureScreen(screens, variables = []) {
  const wanted = new Set(variables.map(normalizeVariable).filter(Boolean))
  if (!wanted.size) return -1
  return screens.findIndex((screen) => (
    screen.type === 'form' && (screen.value?.fields || []).some((field) => (
      fieldVariables(field).some((variable) => wanted.has(variable))
    ))
  ))
}

function groupWindowLines(fields = []) {
  const lines = new Map()
  fields.filter((field) => field && field.visible !== false).forEach((field, index) => {
    const key = field.line ?? field.sourceLine ?? `unpositioned-${index}`
    const entry = lines.get(key) || { key, line: field.line ?? field.sourceLine, fields: [] }
    entry.fields.push(field)
    lines.set(key, entry)
  })
  return [...lines.values()].sort((left, right) => {
    if (left.line == null) return 1
    if (right.line == null) return -1
    return left.line - right.line
  })
}

function runtimeValue(values, binding) {
  const wanted = normalizeVariable(binding)
  const key = Object.keys(values || {}).find((candidate) => normalizeVariable(candidate) === wanted)
  return key ? values[key] : undefined
}

function updateRuntimeValue(values, binding, value) {
  const wanted = normalizeVariable(binding)
  const key = Object.keys(values || {}).find((candidate) => normalizeVariable(candidate) === wanted) || binding
  return { ...values, [key]: value }
}

function traceEventLabel(event = {}) {
  const type = String(event.type || 'event').toLowerCase()
  const labels = {
    start: 'INICIO',
    'form-submit': 'FORMULARIO',
    'button-press': 'BOTÓN',
    'menu-choice': 'MENÚ',
    showwindow: 'VENTANA',
    window: 'VENTANA',
    showmenu: 'MENÚ',
    call: 'CALL',
    goto: 'GOTO',
    return: 'RETURN',
    if: 'IF',
    require: 'REQUIRE',
    default: 'DEFAULT',
    compute: 'CÁLCULO',
    command: 'COMANDO',
    remark: 'REMARK',
    ssr: 'SSR',
    read: 'READ',
    write: 'WRITE',
    'command-buffer': 'BUFFER',
    'emulator-control': 'CONTROL',
    diagnostic: 'DETENIDO',
    finish: 'FIN',
  }
  return labels[type] || type.toUpperCase()
}

function traceEventDetail(event = {}) {
  const type = String(event.type || '').toLowerCase()
  if (type === 'if') return event.matched ? 'Condición verdadera → THEN' : 'Condición falsa → ELSE'
  if (type === 'require') return event.valid ? `Validación correcta: ${event.target || ''}` : `Falta valor: ${event.target || ''}`
  if (type === 'form-submit') return event.fields?.length ? `Campos enviados: ${event.fields.join(', ')}` : 'Formulario confirmado'
  if (type === 'button-press') return event.target ? `Botón ${event.target}` : 'Botón presionado'
  if (type === 'menu-choice') return event.label || event.target || 'Opción seleccionada'
  if (type === 'diagnostic') return event.message || 'El flujo fue detenido'
  if (type === 'finish') return 'El flujo terminó sin enviar operaciones a Sabre'
  if (['command', 'remark', 'ssr', 'read', 'write', 'command-buffer'].includes(type)) {
    return event.text || event.target || '(sin contenido)'
  }
  if (type === 'emulator-control') return event.control || event.target || 'Control local'
  if (event.target) return `${event.resolved === false ? 'Destino no encontrado: ' : ''}${event.target}`
  if (event.value !== undefined) return `${event.target || 'valor'} = ${String(event.value)}`
  return event.message || ''
}

function navigationTraceEvents(events = []) {
  return events.map((event) => ({
    ...event,
    label: traceEventLabel(event),
    detail: traceEventDetail(event),
  }))
}

function controlType(field = {}) {
  return String(field.controlType || field.control || field.typedata || field.type || 'text').toLowerCase()
}

function fieldLabel(field = {}) {
  return field.label || field.text || field.name || field.binding || field.variable || ''
}

function SstControl({ field, values, errors, onChange, onButton, compact = false }) {
  const binding = getFieldBinding(field)
  const type = controlType(field)
  const current = runtimeValue(values, binding)
  const value = current ?? field.value ?? ''
  const error = runtimeValue(errors, binding)
  const options = field.options || field.datasource || []
  const disabled = field.disabled === true
  const readOnly = field.readonly === true || field.editable === false

  if (field.type === 'button') {
    return (
      <button type="button" className="sst-native-btn is-primary" onClick={() => onButton(field)}>
        {fieldLabel(field) || String(field.action || '').replace(/^@/, '')}
      </button>
    )
  }
  if (field.type === 'literal') return <span className="sst-native-literal">{field.value}</span>
  if (field.type === 'system' || type === 'display' || type === 'label') {
    return <span className="sst-native-display">{value === '' ? '—' : String(value)}</span>
  }
  if (type === 'checkbox' || /#C$/i.test(field.variable || field.binding || '')) {
    return (
      <input
        type="checkbox"
        checked={isSstCheckboxChecked(value)}
        disabled={disabled || readOnly}
        onChange={(event) => onChange(binding, event.target.checked)}
      />
    )
  }
  if (options.length || type === 'select') {
    return (
      <span className="sst-native-control-wrap">
        <select
          className="form-select form-select-sm"
          value={value}
          disabled={disabled || readOnly}
          onChange={(event) => onChange(binding, event.target.value)}
        >
          <option value="">Seleccionar…</option>
          {options.map((option, index) => (
            <option key={`${binding}-${option.value ?? index}`} value={option.value ?? option.label ?? option.text}>
              {option.label || option.text || option.value}
            </option>
          ))}
        </select>
        {error ? <small className="sst-native-error">{error}</small> : null}
      </span>
    )
  }
  return (
    <span className="sst-native-control-wrap">
      <input
        className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
        type="text"
        value={value}
        maxLength={field.maxlength || field.length || undefined}
        readOnly={readOnly}
        disabled={disabled}
        style={compact ? { minWidth: `${Math.min(Math.max(Number(field.maxlength || 8), 6), 24)}ch` } : undefined}
        onChange={(event) => onChange(binding, event.target.value)}
      />
      {error ? <small className="sst-native-error">{error}</small> : null}
    </span>
  )
}

function SstField({ field, values, errors, onChange, onButton }) {
  if (!field || field.visible === false) return null
  if (field.type === 'text') return <div className="sst-native-copy">{fieldLabel(field)}</div>
  if (field.type === 'button') {
    return <div className="sst-native-actions"><SstControl field={field} values={values} errors={errors} onChange={onChange} onButton={onButton} /></div>
  }
  if (field.type === 'field_row' || controlType(field) === 'field_row') {
    return (
      <div className="sst-native-row">
        <span className="sst-native-label">{fieldLabel(field)}</span>
        <span className="sst-native-composite">
          {(field.parts || []).filter((part) => part.visible !== false).map((part, index) => (
            <SstControl key={`${getFieldBinding(part)}-${index}`} compact field={part} values={values} errors={errors} onChange={onChange} onButton={onButton} />
          ))}
        </span>
      </div>
    )
  }
  if (field.type === 'checkbox-group' || controlType(field) === 'checkbox-group') {
    return (
      <div className="sst-native-row">
        <span className="sst-native-label">{fieldLabel(field)}</span>
        <span className="sst-native-checkboxes">
          {(field.options || []).filter((option) => option.visible !== false).map((option, index) => (
            <label key={`${option.variable || option.label}-${index}`}>
              <SstControl field={{ ...option, type: 'checkbox', controlType: 'checkbox' }} values={values} errors={errors} onChange={onChange} onButton={onButton} />
              <span>{fieldLabel(option)}</span>
            </label>
          ))}
        </span>
      </div>
    )
  }
  return (
    <div className="sst-native-row">
      <span className="sst-native-label">
        {fieldLabel(field)}{(field.required || field.mandatory || field.visualRequired) ? <b className="sst-native-required"> *</b> : null}
      </span>
      <SstControl field={field} values={values} errors={errors} onChange={onChange} onButton={onButton} />
    </div>
  )
}

export default function SstInterpreterPreview({ ir = {}, logicJson = {}, fileName = '' }) {
  const screens = useMemo(() => buildScreens(ir), [ir])
  const [activeIndex, setActiveIndex] = useState(-1)
  const [cursor, setCursor] = useState(null)
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})
  const [diagnostic, setDiagnostic] = useState(null)
  const [finished, setFinished] = useState(false)
  const [history, setHistory] = useState([])
  const [safeOperations, setSafeOperations] = useState([])
  const [flowTrace, setFlowTrace] = useState([])

  const drive = useCallback((initialExecution) => {
    let execution = initialExecution
    const traceBatch = []
    const flushTrace = () => {
      if (traceBatch.length) setFlowTrace((current) => [...current, ...traceBatch])
    }
    for (let step = 0; execution && step < 500; step += 1) {
      traceBatch.push(...navigationTraceEvents(execution.events || []))
      if (execution.serviceRequest) {
        const action = execution.serviceRequest
        const preparedCommands = previewSstCommands(action, execution.values || {})
        const operationTexts = preparedCommands.length
          ? preparedCommands
          : [action.expression || action.target || action.raw || action.template || '']
        setSafeOperations((current) => [...current, ...operationTexts.filter(Boolean).map((text) => ({
          type: action.type,
          text,
          sourceLine: action.sourceLine || action.line,
        }))])
        const operationText = operationTexts.filter(Boolean).join(' → ')
        const lastTrace = traceBatch[traceBatch.length - 1]
        if (lastTrace && String(lastTrace.type || '').toLowerCase() === String(action.type || '').toLowerCase()) {
          lastTrace.text = operationText
          lastTrace.detail = operationText || '(sin contenido)'
          lastTrace.simulated = true
        }
        execution = advanceNavigation(logicJson, execution.cursor, execution.values || {})
        continue
      }
      setCursor(execution.cursor)
      setValues(execution.values || {})
      if (execution.diagnostic || execution.unresolvedTarget || execution.error) {
        const issue = execution.diagnostic || {
          kind: 'runtime-error',
          message: execution.error || `No se encontró el destino ${execution.unresolvedTarget || ''}.`,
        }
        const captureIndex = findCaptureScreen(screens, issue.missingVariables || [])
        if (captureIndex >= 0) {
          setActiveIndex(captureIndex)
          setValues(mergeFormDefaults(execution.values || {}, screens[captureIndex].value || {}))
          setCursor(execution.retryCursor || execution.cursor)
          setErrors(Object.fromEntries((issue.missingVariables || []).map((name) => [normalizeVariable(name), 'Dato requerido por el flujo SST.'])))
          setDiagnostic(null)
        } else {
          setDiagnostic(issue)
        }
        traceBatch.push({
          type: 'diagnostic',
          label: traceEventLabel({ type: 'diagnostic' }),
          detail: issue.message || 'El flujo fue detenido',
          block: issue.block,
          sourceLine: issue.sourceLine,
        })
        setFinished(false)
        flushTrace()
        return
      }
      if (execution.finished) {
        setFinished(true)
        setDiagnostic(null)
        traceBatch.push({
          type: 'finish',
          label: traceEventLabel({ type: 'finish' }),
          detail: traceEventDetail({ type: 'finish' }),
        })
        flushTrace()
        return
      }
      if (execution.screenRequest) {
        const index = findTargetIndex(screens, execution.screenRequest)
        if (index < 0) {
          setDiagnostic({
            kind: 'screen-not-found',
            message: `El flujo solicitó ${execution.screenRequest.target || execution.screenRequest.legacyTarget || 'una pantalla'} que no está en el IR.`,
          })
          traceBatch.push({
            type: 'diagnostic',
            label: traceEventLabel({ type: 'diagnostic' }),
            detail: `Pantalla no encontrada: ${execution.screenRequest.target || execution.screenRequest.legacyTarget || '(sin destino)'}`,
          })
          flushTrace()
          return
        }
        setActiveIndex(index)
        setValues(mergeFormDefaults(execution.values || {}, screens[index].value || {}))
        setErrors({})
        setDiagnostic(null)
        setFinished(false)
        flushTrace()
        return
      }
      flushTrace()
      return
    }
    flushTrace()
    setDiagnostic({ kind: 'step-limit', message: 'La simulación se detuvo por límite de pasos automáticos.' })
  }, [logicJson, screens])

  const restart = useCallback(() => {
    setHistory([])
    setSafeOperations([])
    setFlowTrace([{
      type: 'start',
      label: traceEventLabel({ type: 'start' }),
      detail: `Bloque inicial: ${logicJson.entryBlock || '(sin bloque)'}`,
      block: logicJson.entryBlock,
    }])
    setErrors({})
    setDiagnostic(null)
    setFinished(false)
    const initialCursor = createNavigationCursor(logicJson)
    const execution = advanceNavigation(logicJson, initialCursor, {})
    if (!logicJson.entryBlock || !logicJson.blocks?.[logicJson.entryBlock]) {
      setCursor(initialCursor)
      setValues({})
      setActiveIndex(screens.length ? 0 : -1)
      setFinished(!screens.length)
      return
    }
    drive(execution)
  }, [drive, logicJson, screens])

  useEffect(() => {
    restart()
  }, [restart])

  const activeScreen = screens[activeIndex]
  const snapshot = () => setHistory((current) => [...current, {
    activeIndex,
    cursor,
    values: { ...values },
    errors: { ...errors },
    diagnostic,
    finished,
    traceLength: flowTrace.length,
  }])

  const continueFlow = (nextValues = values) => {
    if (!cursor) return
    snapshot()
    setErrors({})
    setDiagnostic(null)
    drive(advanceNavigation(logicJson, cursor, nextValues))
  }

  const submitForm = () => {
    const nextErrors = validateSstForm(activeScreen?.value || {}, values)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    const fields = (activeScreen?.value?.fields || [])
      .flatMap(fieldVariables)
      .filter((name) => runtimeValue(values, name) !== undefined)
    setFlowTrace((current) => [...current, {
      type: 'form-submit',
      label: traceEventLabel({ type: 'form-submit' }),
      detail: traceEventDetail({ type: 'form-submit', fields }),
      target: activeScreen?.value?.legacyId || activeScreen?.id,
      fields,
    }])
    continueFlow(values)
  }

  const pressButton = (field) => {
    const action = String(field.action || field.button || field.variable || '@OK').replace(/^@/, '').toUpperCase()
    const nextValues = { ...values, BTN_PRSD: action, '@BTN_PRSD': action }
    setValues(nextValues)
    setFlowTrace((current) => [...current, {
      type: 'button-press',
      label: traceEventLabel({ type: 'button-press' }),
      detail: traceEventDetail({ type: 'button-press', target: action }),
      target: action,
      sourceLine: field.sourceLine || field.line,
    }])
    continueFlow(nextValues)
  }

  const chooseMenu = (item) => {
    if (!cursor) return
    snapshot()
    setFlowTrace((current) => [...current, {
      type: 'menu-choice',
      label: traceEventLabel({ type: 'menu-choice' }),
      detail: traceEventDetail({ type: 'menu-choice', label: item.text || item.label || item.value }),
      target: item.target || item.actionRef,
      sourceLine: item.sourceLine || item.line,
    }])
    const submenu = item.submenu || item.actions?.find((action) => action.type === 'submenu')?.menu
    if (submenu) {
      const submenuIndex = findTargetIndex(screens, { type: 'showMenu', target: submenu.id || submenu.key, title: submenu.title })
      if (submenuIndex >= 0) {
        setActiveIndex(submenuIndex)
        setErrors({})
        setDiagnostic(null)
        return
      }
    }
    const actions = getMenuActions(logicJson, item)
    if (!actions.length) {
      setDiagnostic({ kind: 'menu-without-action', message: `La opción “${item.text || item.label || item.value}” no tiene una acción asociada.` })
      return
    }
    drive(advanceNavigation(logicJson, injectActions(cursor, actions, `menu:${item.actionRef || item.text || 'option'}`), values))
  }

  const goBack = () => {
    const previous = history[history.length - 1]
    if (!previous) return
    setHistory((current) => current.slice(0, -1))
    setActiveIndex(previous.activeIndex)
    setCursor(previous.cursor)
    setValues(previous.values)
    setErrors(previous.errors)
    setDiagnostic(previous.diagnostic)
    setFinished(previous.finished)
    setFlowTrace((current) => current.slice(0, previous.traceLength ?? current.length))
  }

  return (
    <div className="sst-native-preview">
      <header className="sst-native-app-header">
        <div className="sst-native-app-identity">
          <span className="sst-native-app-icon" aria-hidden="true">▤</span>
          <div>
            <strong>{fileName || ir.title || 'Script SST'}</strong>
            <small>Simulación determinista SST</small>
          </div>
        </div>
        <span className="sst-native-safe-badge">Vista segura</span>
      </header>

      <div className="sst-native-stepbar" aria-label="Estado de la simulación">
        <div className="sst-native-step is-done"><span>✓</span><b>Validación</b></div>
        <i />
        <div className={`sst-native-step ${finished ? 'is-done' : 'is-active'}`}><span>{finished ? '✓' : '2'}</span><b>Completar datos</b></div>
        <i />
        <div className={`sst-native-step ${finished ? 'is-done' : ''}`}><span>{finished ? '✓' : '3'}</span><b>Revisar</b></div>
        <i />
        <div className={`sst-native-step ${finished ? 'is-active' : ''}`}><span>4</span><b>Resultado</b></div>
      </div>

      <main className="sst-native-content">
        <div className="sst-native-safe-note">
          <span aria-hidden="true">⚠</span>
          <span>Los comandos se interpretan y se muestran para revisión, pero no se envían a Sabre.</span>
        </div>

        {diagnostic ? (
          <div className="sst-native-diagnostic">
            <strong>{diagnostic.kind || 'Flujo detenido'}</strong>
            <span>{diagnostic.message}</span>
          </div>
        ) : null}

        {finished ? (
          <section className="sst-native-window sst-native-result-card">
            <header>
              <strong><span aria-hidden="true">✓</span> Script finalizado</strong>
            </header>
            <div className="sst-native-window-body">
              <div className="sst-native-finished">
                <strong>Fin del flujo SST</strong>
                <span>La simulación terminó sin ejecutar operaciones Sabre.</span>
              </div>
            </div>
            <footer>
              <button type="button" className="sst-native-btn is-secondary" onClick={restart}>↻ Reiniciar</button>
            </footer>
          </section>
        ) : activeScreen ? (
          <section className="sst-native-window">
            <header>
              <strong><span aria-hidden="true">✎</span> {activeScreen.title || activeScreen.id}</strong>
              <small>Paso {Math.max(1, history.length + 1)} · {activeScreen.type === 'menu' ? 'Menú' : activeScreen.value?.legacyId || activeScreen.id}</small>
            </header>
            <div className="sst-native-window-body">
              {activeScreen.type === 'menu' ? (
                <div className="sst-native-menu">
                  {(activeScreen.value?.items || activeScreen.value?.options || []).filter((item) => item.visible !== false).map((item, index) => (
                    <button type="button" key={`${item.value || item.text}-${index}`} onClick={() => chooseMenu(item)}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      {item.text || item.label || item.value}
                    </button>
                  ))}
                </div>
              ) : (
                groupWindowLines(activeScreen.value?.fields || []).map((line) => (
                  <div className="sst-native-line" key={line.key}>
                    {line.fields.map((field, index) => (
                      <SstField
                        key={`${field.variable || field.action || field.label || field.type}-${index}`}
                        field={field}
                        values={values}
                        errors={errors}
                        onButton={pressButton}
                        onChange={(binding, value) => setValues((current) => updateRuntimeValue(current, binding, value))}
                      />
                    ))}
                  </div>
                ))
              )}
            </div>
            <footer>
              <button type="button" className="sst-native-btn is-secondary" onClick={restart}>↻ Reiniciar</button>
              <div>
                <button type="button" className="sst-native-btn is-secondary" disabled={!history.length} onClick={goBack}>← Anterior</button>
                {activeScreen.type === 'form' && !activeScreen.value?.fields?.some((field) => field.type === 'button' && field.visible !== false) ? (
                  <button type="button" className="sst-native-btn is-primary" onClick={submitForm}>Continuar →</button>
                ) : null}
              </div>
            </footer>
          </section>
        ) : (
          <div className="sst-native-diagnostic">No se detectó una pantalla inicial.</div>
        )}

        {safeOperations.length ? (
          <details className="sst-native-operations">
            <summary>{safeOperations.length} operación(es) interpretada(s) de forma segura</summary>
            {safeOperations.slice(-12).map((operation, index) => (
              <div key={`${operation.type}-${operation.sourceLine}-${index}`}>
                <strong>{String(operation.type || '').toUpperCase()}</strong>
                {operation.sourceLine ? <span>línea {operation.sourceLine}</span> : null}
                <code>{operation.text || '(sin contenido)'}</code>
              </div>
            ))}
          </details>
        ) : null}

        {flowTrace.length ? (
          <details className="sst-native-trace" open>
            <summary>Traza de ejecución ({flowTrace.length} evento{flowTrace.length === 1 ? '' : 's'})</summary>
            <div className="sst-native-trace-list">
              {flowTrace.slice(-80).map((event, index) => (
                <div className={`sst-native-trace-row is-${String(event.type || 'event').toLowerCase()}`} key={`${event.type}-${event.actionId || ''}-${event.sourceLine || ''}-${index}`}>
                  <span className="sst-native-trace-step">{Math.max(1, flowTrace.length - Math.min(flowTrace.length, 80) + index + 1)}</span>
                  <strong>{event.label || traceEventLabel(event)}</strong>
                  <span className="sst-native-trace-detail">{event.detail || traceEventDetail(event)}</span>
                  <small>
                    {event.block ? `bloque ${event.block}` : ''}
                    {event.block && event.sourceLine ? ' · ' : ''}
                    {event.sourceLine ? `línea ${event.sourceLine}` : ''}
                    {event.simulated ? `${event.block || event.sourceLine ? ' · ' : ''}simulado` : ''}
                  </small>
                </div>
              ))}
            </div>
          </details>
        ) : null}
      </main>
    </div>
  )
}
